/**
 * A figure against the ceiling it is measured towards.
 *
 * Screens carried three of these before it existed — margin against available
 * margin, drawdown against its limit, slots against the pool — and each drew
 * its own track, its own height and its own idea of which colour a proportion
 * takes. The one that mattered was the colour: a utilisation bar painted in
 * the result scale says the account is losing money when all it says is that
 * capital is working.
 *
 * The ceiling is written beside the bar, never only inside it. A bar without
 * its limit spelled out is a shape, and §6 of the design contract already
 * requires a percentage to name its base on screen rather than in a tooltip.
 *
 * Contract §4.1.
 */

/** What the proportion means, which decides what colour it takes. */
export type LimitTone =
  /** Capital doing its job: used margin, filled slots, a grid in progress. */
  | "neutral"
  /** A figure moving the wrong way against a limit: drawdown, exposure. */
  | "adverse";

const FILL: Record<LimitTone, string> = {
  // The proportion scale, never the result scale. Since 2026-10-03 it takes
  // the terminal mocks' pale blue in the dark theme, as their margin and
  // utilisation bars do.
  neutral: "bg-proportion-fill",
  adverse: "bg-status-negative",
};

type Props = {
  /**
   * How much of the track is filled, as a whole percentage in a string —
   * exactly what `percentOf` returns. A string rather than a number on
   * purpose: every proportion on these screens comes from two Decimal money
   * values, and parsing one into a float to draw a bar is the same defect as
   * parsing one to print it. `percentOf` already clamps at 100.
   */
  readonly filled: string;
  /** The ceiling in words — `Лимит: −15%`, `83% использовано`. Required,
      because the bar alone does not say what it is a share of. */
  readonly label: string;
  readonly tone?: LimitTone;
  readonly testId?: string | undefined;
};

export function LimitBar({
  filled,
  label,
  tone = "neutral",
  testId,
}: Props): React.JSX.Element {
  return (
    <div className="mt-2 flex flex-col gap-1" data-testid={testId}>
      {/* The track is a surface, not a border: at the new hairline weight a
          1.22:1 outline cannot describe a 6px shape. */}
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-surface-hover"
        role="img"
        aria-label={label}
      >
        <div
          className={`h-full ${FILL[tone]}`}
          style={{ width: `${filled}%` }}
          data-testid={testId === undefined ? undefined : `${testId}-fill`}
        />
      </div>
      <p className="text-helper-sm text-text-secondary">{label}</p>
    </div>
  );
}
